/**
 * B-24b WS-3 — TanStack Query hooks for the SA Operator Console dashboard.
 *
 * Each hook maps 1:1 to a WS-2 endpoint. WS-2 is in flight; responses come
 * from vi.mock(apiClient) in tests. When WS-2 merges the mocks are replaced
 * transparently — the hook signatures stay stable.
 *
 * staleTime = 60s (matches the existing dashboardApi panel pattern).
 * refetchInterval = 60s on kpis + attention so the front door stays live
 * without a manual refresh.
 */
import { useQuery } from "@tanstack/react-query";
import {
  fetchSaKpis,
  fetchSaAttention,
  fetchSaUsageTrend,
  fetchSaCalibrationHealth,
  fetchSaAccountHealth,
  fetchSaUsageRows,
  type SaKpisResponse,
  type SaAttentionResponse,
  type SaUsageTrendResponse,
  type SaCalibrationHealthResponse,
  type SaAccountHealthResponse,
  type SaUsageRowsResponse,
} from "@/lib/saDashboardApi";

const SA_DASHBOARD_KEYS = {
  kpis: ["sa-dashboard", "kpis"] as const,
  attention: ["sa-dashboard", "attention"] as const,
  usageTrend: ["sa-dashboard", "usage-trend"] as const,
  calibrationHealth: ["sa-dashboard", "calibration-health"] as const,
  accountHealth: ["sa-dashboard", "account-health"] as const,
  usageRows: (requestId: string) =>
    ["sa-dashboard", "usage-rows", requestId] as const,
} as const;

export function useSaKpis() {
  return useQuery<SaKpisResponse, Error>({
    queryKey: SA_DASHBOARD_KEYS.kpis,
    queryFn: fetchSaKpis,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useSaAttention() {
  return useQuery<SaAttentionResponse, Error>({
    queryKey: SA_DASHBOARD_KEYS.attention,
    queryFn: fetchSaAttention,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useSaUsageTrend() {
  return useQuery<SaUsageTrendResponse, Error>({
    queryKey: SA_DASHBOARD_KEYS.usageTrend,
    queryFn: fetchSaUsageTrend,
    staleTime: 60_000,
  });
}

export function useSaCalibrationHealth() {
  return useQuery<SaCalibrationHealthResponse, Error>({
    queryKey: SA_DASHBOARD_KEYS.calibrationHealth,
    queryFn: fetchSaCalibrationHealth,
    staleTime: 60_000,
  });
}

export function useSaAccountHealth() {
  return useQuery<SaAccountHealthResponse, Error>({
    queryKey: SA_DASHBOARD_KEYS.accountHealth,
    queryFn: fetchSaAccountHealth,
    staleTime: 60_000,
  });
}

/**
 * Per-request cost drill-down. Enabled only once a (trimmed) request_id is
 * supplied — an empty input must never fire the GET.
 */
export function useSaUsageRows(requestId: string) {
  const trimmed = requestId.trim();
  return useQuery<SaUsageRowsResponse, Error>({
    queryKey: SA_DASHBOARD_KEYS.usageRows(trimmed),
    queryFn: () => fetchSaUsageRows(trimmed),
    enabled: trimmed.length > 0,
    staleTime: 60_000,
  });
}
